import React, { useState, useMemo } from 'react';
import TopBar from '../layout/TopBar';
import StatusBadge from '../components/StatusBadge';
import UploadMasterDialog, { downloadCsvTemplate } from '../components/UploadMasterDialog';
import ViewMappingDialog from '../components/ViewMappingDialog';
import SchemaDiffViewer from '../components/SchemaDiffViewer';
import { MODULE_SCHEMAS, DEMO_FIXTURES } from '../data/masterData';
import { useHGS } from '../context/HGSContext';
import {
  Upload, Download, FileSpreadsheet, ShieldAlert, ClipboardCheck, ShieldCheck, FileSignature,
  Plus, Edit2, Trash2, Search, ArrowUp, ArrowDown, CheckCircle2, ToggleLeft, ToggleRight,
  Code2, Clock, User as UserIcon, FileText, History, X, Wand2, GitCompare,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const STATUS_PILL = {
  Active: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500' },
  Draft: { bg: 'bg-amber-50', text: 'text-amber-800', border: 'border-amber-200', dot: 'bg-amber-500' },
  Pending: { bg: 'bg-violet-50', text: 'text-violet-800', border: 'border-violet-200', dot: 'bg-violet-500' },
  Error: { bg: 'bg-red-50', text: 'text-[#CC0000]', border: 'border-red-200', dot: 'bg-red-500' },
  'Not Uploaded': { bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-200', dot: 'bg-slate-400' },
};

function StatusPill({ status }) {
  const s = STATUS_PILL[status] || STATUS_PILL.Draft;
  return (
    <span
      className={cn('inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] font-bold', s.bg, s.text, s.border)}
      data-testid={`master-status-${status}`}
    >
      <span className={cn('w-1.5 h-1.5 rounded-full', s.dot)} />
      {status}
    </span>
  );
}

function VersionHistoryDialog({ open, moduleKey, history, onClose }) {
  if (!open) return null;
  const schema = MODULE_SCHEMAS[moduleKey];
  const items = history || [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
      onClick={onClose}
      data-testid="version-history-dialog"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: schema.color + '15', color: schema.color }}
            >
              <History className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base font-bold text-slate-900">{schema.label} — Version History</h2>
              <p className="text-xs text-slate-500">{items.length} versions logged · audit trail</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500"
            data-testid="version-history-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {items.length === 0 ? (
            <div className="p-8 text-center text-xs text-slate-500">No versions yet</div>
          ) : (
            <ol className="relative border-l-2 border-slate-200 ml-3 space-y-5">
              {items.map((v, i) => (
                <li key={v.version + i} className="ml-5" data-testid={`history-item-${i}`}>
                  <span className={cn(
                    'absolute -left-[9px] w-4 h-4 rounded-full border-2 border-white',
                    v.status === 'Active' ? 'bg-emerald-500' : v.status === 'Draft' ? 'bg-amber-500' : 'bg-slate-400'
                  )} />
                  <div className="rounded-xl border border-slate-200 bg-white p-4 hover:shadow-sm transition">
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-bold text-slate-900">{v.version}</span>
                        <StatusPill status={v.status} />
                      </div>
                      <span className="text-[10px] text-slate-500 font-mono">{v.publishedAt}</span>
                    </div>
                    <div className="text-xs text-slate-700 mb-2">{v.notes}</div>
                    <div className="flex items-center gap-3 text-[10px] text-slate-600">
                      <span className="flex items-center gap-1"><UserIcon className="w-3 h-3 text-slate-400" />{v.publishedBy}</span>
                      <span className="flex items-center gap-1"><FileSpreadsheet className="w-3 h-3 text-slate-400" />{v.recordCount} records</span>
                      {(v.added != null || v.updated != null) && (
                        <span className="flex items-center gap-1 text-emerald-700">
                          +{v.added || 0} added · {v.updated || 0} updated
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-100 bg-white flex items-center justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-semibold rounded-lg transition"
            data-testid="version-history-done"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

const MODULE_META = [
  { key: 'risk', name: 'Risk Assessment Master', subtitle: 'Risk register & taxonomy', icon: ShieldAlert, gradient: 'from-red-50 to-red-100/50', accent: '#CC0000', border: 'border-red-200' },
  { key: 'pcc', name: 'Product Compliance Data Master', subtitle: 'Section-wise compliance questions', icon: ClipboardCheck, gradient: 'from-slate-50 to-slate-100/50', accent: '#1F2937', border: 'border-slate-200' },
  { key: 'fraud', name: 'Fraud Prevention Master', subtitle: 'Control checklist library', icon: ShieldCheck, gradient: 'from-sky-50 to-sky-100/50', accent: '#0EA5E9', border: 'border-sky-200' },
  { key: 'dlc', name: 'DLC Master', subtitle: 'Quarterly declaration question bank', icon: FileSignature, gradient: 'from-violet-50 to-violet-100/50', accent: '#7C3AED', border: 'border-violet-200' },
];

function RowEditModal({ open, schema, row, onClose, onSave }) {
  const [draft, setDraft] = useState(row || {});
  React.useEffect(() => { setDraft(row || {}); }, [row]);
  if (!open || !schema) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="text-base font-bold text-slate-900">{row?.[schema.idField] ? 'Edit' : 'Add'} {schema.label.replace(' Master', '')} item</h3>
            <p className="text-xs text-slate-500">Fill or update the configuration below</p>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900 text-2xl leading-none">×</button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          {schema.fields.map((f) => (
            <div key={f.key} className={f.key === 'requirement' || f.key === 'controlQuestion' || f.key === 'declarationQuestion' || f.key === 'riskItem' ? 'md:col-span-2' : ''}>
              <label className="text-[10px] font-semibold text-slate-700 uppercase tracking-wider mb-1 flex items-center gap-1">
                {f.label}
                {f.mandatory && <span className="text-[#CC0000]">*</span>}
              </label>
              {f.options ? (
                <select
                  value={draft[f.key] || ''}
                  onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                  className="w-full px-3 py-2 text-xs bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-[#CC0000] focus:ring-2 focus:ring-[#CC0000]/10"
                >
                  <option value="">— Select —</option>
                  {f.options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input
                  value={draft[f.key] || ''}
                  onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                  placeholder={f.sample}
                  className="w-full px-3 py-2 text-xs bg-white border border-slate-200 rounded-lg focus:outline-none focus:border-[#CC0000] focus:ring-2 focus:ring-[#CC0000]/10"
                />
              )}
            </div>
          ))}
        </div>
        <div className="px-6 py-3 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-3 py-2 text-xs font-semibold text-slate-600 hover:text-slate-900">Cancel</button>
          <button
            onClick={() => { onSave({ ...draft, active: draft.active ?? true }); onClose(); }}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#CC0000] hover:bg-[#B00000] text-white text-xs font-semibold rounded-lg shadow-sm"
          >
            <CheckCircle2 className="w-3.5 h-3.5" />
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

function MasterTable({ moduleKey, rows, onChange }) {
  const schema = MODULE_SCHEMAS[moduleKey];
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState(null); // null | {row, isNew}

  const filtered = useMemo(() => {
    if (!search.trim()) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) => Object.values(r).some((v) => String(v).toLowerCase().includes(q)));
  }, [rows, search]);

  const move = (idx, dir) => {
    const next = [...rows];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    onChange(next);
  };

  const toggleActive = (idx) => {
    const next = [...rows];
    next[idx] = { ...next[idx], active: !next[idx].active };
    onChange(next);
  };

  const remove = (idx) => {
    if (!confirm('Delete this question? This cannot be undone in this demo.')) return;
    const next = rows.filter((_, i) => i !== idx);
    onChange(next);
    toast.success('Question deleted');
  };

  const saveRow = (draft) => {
    if (editing?.isNew) {
      onChange([...rows, draft]);
      toast.success('Question added');
    } else {
      const idx = rows.findIndex((r) => r[schema.idField] === editing.row[schema.idField]);
      const next = [...rows];
      next[idx] = draft;
      onChange(next);
      toast.success('Question updated');
    }
  };

  // Show first 5 fields as columns + actions
  const cols = schema.fields.slice(0, 5);

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search master..."
              className="pl-9 pr-3 py-2 text-xs bg-white border border-slate-200 rounded-lg w-64 focus:outline-none focus:border-[#CC0000] focus:ring-2 focus:ring-[#CC0000]/10"
            />
          </div>
          <span className="text-xs text-slate-500">{filtered.length} of {rows.length}</span>
        </div>
        <button
          onClick={() => setEditing({ row: { active: true }, isNew: true })}
          className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#CC0000] hover:bg-[#B00000] text-white text-xs font-semibold rounded-lg shadow-sm"
        >
          <Plus className="w-3.5 h-3.5" />
          Add Question
        </button>
      </div>

      <div className="rounded-lg border border-slate-200 overflow-hidden bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-3 py-2.5 text-[10px] font-semibold text-slate-500 uppercase tracking-wider w-10">Order</th>
                {cols.map((f) => (
                  <th key={f.key} className="px-3 py-2.5 text-left text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                    {f.label}
                  </th>
                ))}
                <th className="px-3 py-2.5 text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                <th className="px-3 py-2.5 w-32" />
              </tr>
            </thead>
            <tbody>
              {filtered.map((row, i) => {
                const realIdx = rows.indexOf(row);
                return (
                  <tr key={row[schema.idField] + i} className={cn('border-t border-slate-100 hover:bg-slate-50/40 transition', !row.active && 'opacity-60')}>
                    <td className="px-3 py-2.5">
                      <div className="flex flex-col gap-0.5">
                        <button onClick={() => move(realIdx, -1)} className="text-slate-400 hover:text-slate-700 disabled:opacity-30" disabled={realIdx === 0}>
                          <ArrowUp className="w-3 h-3" />
                        </button>
                        <button onClick={() => move(realIdx, 1)} className="text-slate-400 hover:text-slate-700 disabled:opacity-30" disabled={realIdx === rows.length - 1}>
                          <ArrowDown className="w-3 h-3" />
                        </button>
                      </div>
                    </td>
                    {cols.map((f) => (
                      <td key={f.key} className="px-3 py-2.5 text-xs text-slate-700 max-w-[260px] truncate" title={row[f.key]}>
                        {f.key === schema.idField ? (
                          <span className="font-mono font-semibold text-slate-900">{row[f.key]}</span>
                        ) : (
                          row[f.key] || <span className="text-slate-300">—</span>
                        )}
                      </td>
                    ))}
                    <td className="px-3 py-2.5">
                      <button onClick={() => toggleActive(realIdx)} className="inline-flex items-center gap-1.5">
                        {row.active ? (
                          <ToggleRight className="w-7 h-7 text-emerald-600" />
                        ) : (
                          <ToggleLeft className="w-7 h-7 text-slate-400" />
                        )}
                        <StatusBadge status={row.active ? 'Active' : 'Inactive'} dot={false} />
                      </button>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-1">
                        <button onClick={() => setEditing({ row, isNew: false })} className="w-7 h-7 rounded-md hover:bg-slate-100 flex items-center justify-center text-slate-500" title="Edit">
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => remove(realIdx)} className="w-7 h-7 rounded-md hover:bg-red-50 flex items-center justify-center text-slate-500 hover:text-[#CC0000]" title="Delete">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={cols.length + 3} className="px-3 py-12 text-center">
                    <FileSpreadsheet className="w-8 h-8 mx-auto text-slate-300 mb-2" />
                    <p className="text-sm font-medium text-slate-500">{rows.length === 0 ? 'No master data yet' : 'No matches'}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{rows.length === 0 ? 'Upload a file or add a question to get started' : 'Try a different search query'}</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <RowEditModal
        open={!!editing}
        schema={schema}
        row={editing?.row}
        onClose={() => setEditing(null)}
        onSave={saveRow}
      />
    </div>
  );
}

export default function MasterConfigPage() {
  const { masters, masterMeta, versionHistory, versionSnapshots, setMasterRows, saveMasterUpload, submitMasterForApproval, loadDemoMaster, user } = useHGS();
  const [uploadFor, setUploadFor] = useState(null);
  const [mappingFor, setMappingFor] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [diffFor, setDiffFor] = useState(null);
  const [tab, setTab] = useState('risk');

  const setRows = (key, rows) => setMasterRows(key, rows);
  const isAdmin = user?.role === 'admin';

  const handleLoadDemo = (moduleKey) => {
    const result = loadDemoMaster(moduleKey);
    if (result) {
      setTab(moduleKey);
      toast.success('Demo master loaded', {
        description: `${result.fileName} · ${result.added} questions ingested at ${result.version}`,
      });
    }
  };

  const handleSaveUpload = (moduleKey, newRows, meta) => {
    const action = meta?.action || 'publish';
    const schema = MODULE_SCHEMAS[moduleKey];
    if (action === 'submit-approval') {
      const sub = submitMasterForApproval(moduleKey, newRows, meta);
      setTab(moduleKey);
      toast.success(`${schema.label} submitted for approval`, {
        description: `${sub.added} added, ${sub.updated} updated · awaiting review by ${sub.reviewer} → check /approvals`,
      });
    } else {
      // 'draft' or 'publish'
      const result = saveMasterUpload(moduleKey, newRows, { ...meta, published: action === 'publish' });
      setTab(moduleKey);
      toast.success(`${schema.label} ${result.published ? 'published' : 'saved as draft'}`, {
        description: `${result.added} added, ${result.updated} updated — total ${result.total} records`,
      });
    }
  };

  return (
    <div>
      <TopBar
        title="Master Configuration"
        subtitle="Upload, manage and version the question bank for every governance module"
        breadcrumbs={['HGS', 'Admin', 'Master Configuration']}
      />

      <div className="p-8 space-y-6">
        {/* Upload cards */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="text-base font-bold text-slate-900">Bulk Upload Masters</h2>
              <p className="text-xs text-slate-500">Upload Excel or CSV files — system will auto-detect columns, validate and generate a JSON Schema</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {MODULE_META.map((m) => {
              const count = masters[m.key].length;
              const active = masters[m.key].filter((r) => r.active).length;
              const meta = masterMeta[m.key] || {};
              return (
                <div
                  key={m.key}
                  className={cn('rounded-xl border bg-gradient-to-br p-5 hover:shadow-md transition flex flex-col', m.gradient, m.border)}
                  data-testid={`master-card-${m.key}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="w-10 h-10 rounded-lg bg-white shadow-sm flex items-center justify-center" style={{ color: m.accent }}>
                      <m.icon className="w-5 h-5" />
                    </div>
                    <div className="text-right">
                      <div className="text-2xl font-bold text-slate-900 leading-none">{count}</div>
                      <div className="text-[10px] text-slate-600 mt-0.5">{active} active</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mb-1">
                    <div className="text-sm font-bold text-slate-900 leading-snug">{m.name}</div>
                  </div>
                  <div className="text-[11px] text-slate-600 mt-0.5 leading-relaxed">{m.subtitle}</div>

                  {/* Meta block */}
                  <div className="mt-3 pt-3 border-t border-white/60 space-y-1.5">
                    <div className="flex items-center justify-between gap-2">
                      <StatusPill status={meta.status || 'Draft'} />
                      <span className="text-[10px] font-mono font-bold text-slate-700 bg-white/70 px-1.5 py-0.5 rounded">
                        {meta.version || 'v1.0'}
                      </span>
                    </div>
                    <div className="flex items-center gap-1.5 text-[10px] text-slate-700" title="Last uploaded">
                      <Clock className="w-3 h-3 text-slate-400 flex-shrink-0" />
                      <span className="font-medium">{meta.lastUploadedAt || '—'}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-[10px] text-slate-600" title="Uploaded by">
                      <UserIcon className="w-3 h-3 text-slate-400 flex-shrink-0" />
                      <span className="truncate">{meta.lastUploadedBy || '—'}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-[10px] text-slate-600" title="Source file">
                      <FileText className="w-3 h-3 text-slate-400 flex-shrink-0" />
                      <span className="truncate font-mono">{meta.fileName || '—'}</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 mt-4">
                    <button
                      onClick={() => setUploadFor(m.key)}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-white text-xs font-semibold rounded-lg shadow-sm transition"
                      style={{ backgroundColor: m.accent }}
                      data-testid={`upload-master-${m.key}`}
                    >
                      <Upload className="w-3.5 h-3.5" />
                      Upload
                    </button>
                    <button
                      onClick={() => setMappingFor(m.key)}
                      className="inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 text-xs font-semibold rounded-lg transition"
                      title="View JSON schema & live form"
                      data-testid={`view-mapping-${m.key}`}
                    >
                      <Code2 className="w-3.5 h-3.5" />
                      View Mapping
                    </button>
                    <button
                      onClick={() => setHistoryFor(m.key)}
                      className="inline-flex items-center justify-center w-9 h-9 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-lg transition"
                      title="View version history"
                      data-testid={`history-${m.key}`}
                    >
                      <History className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => setDiffFor(m.key)}
                      className="inline-flex items-center justify-center w-9 h-9 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-lg transition disabled:opacity-40 disabled:cursor-not-allowed"
                      title="Compare versions side-by-side"
                      data-testid={`diff-${m.key}`}
                      disabled={Object.keys(versionSnapshots?.[m.key] || {}).length < 2}
                    >
                      <GitCompare className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => { downloadCsvTemplate(m.key); toast.success('Template downloaded', { description: `${m.name} CSV template` }); }}
                      className="inline-flex items-center justify-center w-9 h-9 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 rounded-lg transition"
                      title="Download CSV template"
                      data-testid={`template-${m.key}`}
                    >
                      <Download className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  {/* Admin-only: load HMSI demo master fixture (mock-upload of the
                      official Excel file) — only visible when no rows exist yet
                      and a fixture is available for this module. */}
                  {isAdmin && count === 0 && DEMO_FIXTURES[m.key] && (
                    <button
                      onClick={() => handleLoadDemo(m.key)}
                      className="mt-2 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 bg-white hover:bg-amber-50 border border-dashed border-amber-300 text-amber-800 text-[11px] font-semibold rounded-lg transition"
                      data-testid={`load-demo-master-${m.key}`}
                      title="Mock-upload HMSI's official master file for this module"
                    >
                      <Wand2 className="w-3.5 h-3.5" />
                      Load demo master ({DEMO_FIXTURES[m.key].rows.length} questions · {DEMO_FIXTURES[m.key].sheetNames.length} sheets)
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Master tables */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-2 pt-2 border-b border-slate-100 flex gap-1 overflow-x-auto">
            {MODULE_META.map((m) => (
              <button
                key={m.key}
                onClick={() => setTab(m.key)}
                className={cn(
                  'inline-flex items-center gap-2 px-4 py-3 text-xs font-semibold rounded-t-lg transition border-b-2 whitespace-nowrap',
                  tab === m.key ? 'text-[#CC0000] border-[#CC0000] bg-red-50/30' : 'border-transparent text-slate-600 hover:text-slate-900'
                )}
                data-testid={`master-tab-${m.key}`}
              >
                <m.icon className="w-4 h-4" />
                {m.name.replace(' Master', '')}
                <span className="ml-1 px-1.5 py-0.5 rounded-md bg-slate-100 text-slate-700 text-[10px] font-bold">
                  {masters[m.key].length}
                </span>
              </button>
            ))}
          </div>
          <div className="p-5">
            <MasterTable moduleKey={tab} rows={masters[tab]} onChange={(rows) => setRows(tab, rows)} />
          </div>
        </div>
      </div>

      <UploadMasterDialog
        open={!!uploadFor}
        moduleKey={uploadFor}
        onClose={() => setUploadFor(null)}
        onSave={handleSaveUpload}
      />

      <ViewMappingDialog
        open={!!mappingFor}
        moduleKey={mappingFor}
        rows={mappingFor ? masters[mappingFor] : []}
        meta={mappingFor ? masterMeta[mappingFor] : null}
        onClose={() => setMappingFor(null)}
      />

      <VersionHistoryDialog
        open={!!historyFor}
        moduleKey={historyFor}
        history={historyFor ? versionHistory[historyFor] : []}
        onClose={() => setHistoryFor(null)}
      />

      {diffFor && (
        <SchemaDiffViewer moduleKey={diffFor} onClose={() => setDiffFor(null)} />
      )}
    </div>
  );
}
